import React, { FC, useState, useMemo } from "react";
import { Table, Tabs } from "antd";
import { Phase, Task, PhaseUpdate, Work } from "@design-system";
import dayjs from "dayjs";
import { DatePicker } from "../date-picker";
import { VPDLabel } from "~/context/taxonomy-context";
import { rangeUtils, GenericRange } from "../../utils/range-utils";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

type Props = {
  phases: Phase[];
  onUpdatePhase: (phase: PhaseUpdate) => void;
  onUpdateTask: (task: Partial<Task>, phaseId: number) => void;
  currencySymbol: string;
  viewStartDate?: Date;
  viewEndDate?: Date;
};

const initRange = (): GenericRange => ({ lower: 0, upper: 0 });

type TableRow = {
  key: string;
  title: string;
  startDate: string;
  endDate: string;
  rawStartDate: Date | null;
  rawEndDate: Date | null;
  hours: string;
  hourPercentage: string;
  fee: string;
  feePercentage: string;
  gpm: string;
  phaseId?: number;
  level?: string;
};

type WorkGroup = {
  work: Work[];
  totalFee: number;
  totalCost: number;
  totalHours: number;
  feeEarners?: Set<string>;
};

type FeeEarnerTotals = {
  hours: number | GenericRange;
  fee: number | GenericRange;
  cost: number | GenericRange;
};

type GroupTotals = {
  hours: number | GenericRange;
  fee: number | GenericRange;
  cost: number | GenericRange;
  feeEarners: Set<string>;
};

// Calculate financials for work items (single or range)
const calculateFinancials = (
  work: Work[] | null | undefined,
  currencySymbol: string,
  showRange: boolean
) => {
  if (!work?.length) {
    return {
      fee: "-",
      gpm: "-",
      rawFee: 0,
      rawCost: 0,
      hours: "-",
      rawHours: 0,
      feeRange: null,
      costRange: null,
      hoursRange: null,
      gpmRange: null,
    };
  }

  if (showRange) {
    // Sum lower/upper for price, cost, and time
    const totalFeeLower = work.reduce((sum, w) => sum + (w.price?.lower || 0), 0);
    const totalFeeUpper = work.reduce((sum, w) => sum + (w.price?.upper || 0), 0);
    const totalCostLower = work.reduce((sum, w) => sum + (w.cost?.lower || 0), 0);
    const totalCostUpper = work.reduce((sum, w) => sum + (w.cost?.upper || 0), 0);
    const totalHoursLower =
      work.reduce(
        (sum, w) =>
          sum + (w.timeLower != null ? w.timeLower : w.timeSingle != null ? w.timeSingle : 0),
        0
      ) / 60;
    const totalHoursUpper =
      work.reduce(
        (sum, w) =>
          sum + (w.timeUpper != null ? w.timeUpper : w.timeSingle != null ? w.timeSingle : 0),
        0
      ) / 60;

    const gpmLower = rangeUtils.calculateGpm(totalFeeLower, totalCostLower);
    const gpmUpper = rangeUtils.calculateGpm(totalFeeUpper, totalCostUpper);

    return {
      fee: rangeUtils.formatRange(
        totalFeeLower,
        totalFeeUpper,
        (v) => `${currencySymbol}${v.toLocaleString()}`
      ),
      gpm: rangeUtils.formatRange(gpmLower, gpmUpper, (v) => `${Math.round(v)}%`),
      rawFee: { lower: totalFeeLower, upper: totalFeeUpper },
      rawCost: { lower: totalCostLower, upper: totalCostUpper },
      hours: rangeUtils.formatRange(totalHoursLower, totalHoursUpper, (v) => v.toFixed(1)),
      rawHours: { lower: totalHoursLower, upper: totalHoursUpper },
      feeRange: { lower: totalFeeLower, upper: totalFeeUpper },
      costRange: { lower: totalCostLower, upper: totalCostUpper },
      hoursRange: { lower: totalHoursLower, upper: totalHoursUpper },
      gpmRange: { lower: gpmLower, upper: gpmUpper },
    };
  } else {
    const totalFee = work.reduce((sum, w) => sum + (w.price?.single || 0), 0);
    const totalCost = work.reduce((sum, w) => sum + (w.cost?.single || 0), 0);
    const totalHours = work.reduce((sum, w) => sum + (w.timeSingle || 0), 0) / 60;
    const gpm = rangeUtils.calculateGpm(totalFee, totalCost);

    return {
      fee: totalFee ? `${currencySymbol}${totalFee.toLocaleString()}` : "-",
      gpm: totalFee ? `${gpm.toFixed(1)}%` : "-",
      rawFee: totalFee,
      rawCost: totalCost,
      hours: totalHours ? totalHours.toFixed(1) : "-",
      rawHours: totalHours,
      feeRange: null,
      costRange: null,
      hoursRange: null,
      gpmRange: null,
    };
  }
};

const calculatePhaseFinancials = (phase: Phase, currencySymbol: string, showRange: boolean) =>
  calculateFinancials(phase.work, currencySymbol, showRange);

// Helper function to calculate financials for grouped items (similar to LeverageTable)
const calculateGroupFinancials = (
  totals: FeeEarnerTotals,
  overallTotals: FeeEarnerTotals,
  currencySymbol: string,
  showRange: boolean
) => {
  if (!showRange) {
    // Handle the single value case
    const hours = typeof totals.hours === "number" ? totals.hours : 0;
    const fee = typeof totals.fee === "number" ? totals.fee : 0;
    const cost = typeof totals.cost === "number" ? totals.cost : 0;
    const overallHours = typeof overallTotals.hours === "number" ? overallTotals.hours : 0;
    const overallFee = typeof overallTotals.fee === "number" ? overallTotals.fee : 0;

    const gpm = rangeUtils.calculateGpm(fee, cost);

    return {
      hours: hours.toFixed(1),
      hourPercentage: overallHours > 0 ? `${((hours / overallHours) * 100).toFixed(1)}%` : "-",
      fee: `${currencySymbol}${fee.toLocaleString()}`,
      feePercentage: overallFee > 0 ? `${((fee / overallFee) * 100).toFixed(1)}%` : "-",
      gpm: `${gpm.toFixed(1)}%`,
    };
  } else {
    // Handle the range case
    const totalHoursLower = rangeUtils.getValue(totals.hours, "lower");
    const totalHoursUpper = rangeUtils.getValue(totals.hours, "upper");
    const totalFeeLower = rangeUtils.getValue(totals.fee, "lower");
    const totalFeeUpper = rangeUtils.getValue(totals.fee, "upper");
    const totalCostLower = rangeUtils.getValue(totals.cost, "lower");
    const totalCostUpper = rangeUtils.getValue(totals.cost, "upper");

    const overallHoursValue = rangeUtils.getValue(overallTotals.hours, "lower");
    const overallFeeValue = rangeUtils.getValue(overallTotals.fee, "lower");

    const gpmLower = rangeUtils.calculateGpm(totalFeeLower, totalCostLower);
    const gpmUpper = rangeUtils.calculateGpm(totalFeeUpper, totalCostUpper);

    return {
      hours: rangeUtils.formatRange(totalHoursLower, totalHoursUpper, (v) => v.toFixed(1)),
      hourPercentage:
        overallHoursValue > 0
          ? `${((totalHoursLower / overallHoursValue) * 100).toFixed(1)}%`
          : "-",
      fee: rangeUtils.formatRange(
        totalFeeLower,
        totalFeeUpper,
        (v) => `${currencySymbol}${v.toLocaleString()}`
      ),
      feePercentage:
        overallFeeValue > 0 ? `${((totalFeeLower / overallFeeValue) * 100).toFixed(1)}%` : "-",
      gpm: rangeUtils.formatRange(gpmLower, gpmUpper, (v) => `${v.toFixed(1)}%`),
    };
  }
};

export const BudgetTable: FC<Props> = ({
  phases,
  onUpdatePhase,
  onUpdateTask,
  currencySymbol,
  viewStartDate,
  viewEndDate,
}) => {
  const PA_LABEL = VPDLabel("PracticeArea", { textCase: "capitalise" });
  const FEL_LABEL = VPDLabel("FeeEarnerLevel", { textCase: "capitalise" });
  const FE_LABEL = VPDLabel("FeeEarner", { textCase: "capitalise" });

  // Add state for selected tab
  const [selectedTab, setSelectedTab] = useState<string>("by-phase");

  // Determine if we should show ranges (if any work has a range)
  const showRange = phases.some((p) => rangeUtils.hasRangeInWork(p.work));

  // Filter work based on view range if provided
  const filteredWork = useMemo(() => {
    if (!viewStartDate || !viewEndDate) {
      return phases.flatMap((phase) => phase.work || []);
    }

    return phases
      .filter(
        (phase) =>
          phase.startDate &&
          phase.deadlineDate &&
          dayjs(phase.deadlineDate).isAfter(viewStartDate) &&
          dayjs(phase.startDate).isBefore(viewEndDate)
      )
      .flatMap((phase) => phase.work || []);
  }, [phases, viewStartDate, viewEndDate]);

  // Group work by practice area, cohort, and fee earner
  const { practiceAreaGroups, feeEarnerLevelGroups, feeEarnerGroups, totals } = useMemo(() => {
    const practiceAreaMap = new Map<string, WorkGroup>();
    const feeEarnerLevelMap = new Map<string, WorkGroup>();
    const feeEarnerMap = new Map<string, FeeEarnerTotals>();
    let totalFeeLower = 0,
      totalFeeUpper = 0,
      totalFeeSingle = 0;
    let totalCostLower = 0,
      totalCostUpper = 0,
      totalCostSingle = 0;
    let totalHoursLower = 0,
      totalHoursUpper = 0,
      totalHoursSingle = 0;

    filteredWork.forEach((work) => {
      const practiceArea = work.feeEarner?.practiceArea?.name || "Unassigned";
      const feeEarnerLevel = work.feeEarnerLevel?.description || "Unassigned";
      const feeEarnerName = work.feeEarner?.name || "Unassigned";
      const feeSingle = work.price?.single || 0;
      const feeLower = work.price?.lower ?? work.price?.single ?? 0;
      const feeUpper = work.price?.upper ?? work.price?.single ?? 0;
      const costSingle = work.cost?.single || 0;
      const costLower = work.cost?.lower ?? work.cost?.single ?? 0;
      const costUpper = work.cost?.upper ?? work.cost?.single ?? 0;
      const hoursSingle = (work.timeSingle || 0) / 60;
      const hoursLower =
        (work.timeLower != null ? work.timeLower : work.timeSingle != null ? work.timeSingle : 0) /
        60;
      const hoursUpper =
        (work.timeUpper != null ? work.timeUpper : work.timeSingle != null ? work.timeSingle : 0) /
        60;

      // Update practice area group
      const practiceAreaGroup = practiceAreaMap.get(practiceArea) || {
        work: [],
        totalFee: 0,
        totalCost: 0,
        totalHours: 0,
        feeEarners: new Set(),
      };
      practiceAreaGroup.work.push(work);
      practiceAreaGroup.totalFee += feeSingle;
      practiceAreaGroup.totalCost += costSingle;
      practiceAreaGroup.totalHours += hoursSingle;
      practiceAreaGroup.feeEarners = practiceAreaGroup.feeEarners || new Set();
      practiceAreaGroup.feeEarners.add(feeEarnerName);
      practiceAreaMap.set(practiceArea, practiceAreaGroup);

      // Update fee earner level group
      const feeEarnerLevelGroup = feeEarnerLevelMap.get(feeEarnerLevel) || {
        work: [],
        totalFee: 0,
        totalCost: 0,
        totalHours: 0,
        feeEarners: new Set(),
      };
      feeEarnerLevelGroup.work.push(work);
      feeEarnerLevelGroup.totalFee += feeSingle;
      feeEarnerLevelGroup.totalCost += costSingle;
      feeEarnerLevelGroup.totalHours += hoursSingle;
      feeEarnerLevelGroup.feeEarners = feeEarnerLevelGroup.feeEarners || new Set();
      feeEarnerLevelGroup.feeEarners.add(feeEarnerName);
      feeEarnerLevelMap.set(feeEarnerLevel, feeEarnerLevelGroup);

      // Update fee earner group
      const feeEarnerGroup = feeEarnerMap.get(feeEarnerName) || {
        hours: showRange ? initRange() : 0,
        fee: showRange ? initRange() : 0,
        cost: showRange ? initRange() : 0,
      };

      if (showRange) {
        const existingHours = feeEarnerGroup.hours as GenericRange;
        const existingFee = feeEarnerGroup.fee as GenericRange;
        const existingCost = feeEarnerGroup.cost as GenericRange;

        feeEarnerMap.set(feeEarnerName, {
          hours: {
            lower: (existingHours.lower || 0) + hoursLower,
            upper: (existingHours.upper || 0) + hoursUpper,
          },
          fee: {
            lower: (existingFee.lower || 0) + feeLower,
            upper: (existingFee.upper || 0) + feeUpper,
          },
          cost: {
            lower: (existingCost.lower || 0) + costLower,
            upper: (existingCost.upper || 0) + costUpper,
          },
        });
      } else {
        feeEarnerMap.set(feeEarnerName, {
          hours: (feeEarnerGroup.hours as number) + hoursSingle,
          fee: (feeEarnerGroup.fee as number) + feeSingle,
          cost: (feeEarnerGroup.cost as number) + costSingle,
        });
      }

      // Update totals
      totalFeeSingle += feeSingle;
      totalFeeLower += feeLower;
      totalFeeUpper += feeUpper;
      totalCostSingle += costSingle;
      totalCostLower += costLower;
      totalCostUpper += costUpper;
      totalHoursSingle += hoursSingle;
      totalHoursLower += hoursLower;
      totalHoursUpper += hoursUpper;
    });

    return {
      practiceAreaGroups: practiceAreaMap,
      feeEarnerLevelGroups: feeEarnerLevelMap,
      feeEarnerGroups: feeEarnerMap,
      totals: showRange
        ? {
            fee: { lower: totalFeeLower, upper: totalFeeUpper },
            cost: { lower: totalCostLower, upper: totalCostUpper },
            hours: { lower: totalHoursLower, upper: totalHoursUpper },
          }
        : {
            fee: totalFeeSingle,
            cost: totalCostSingle,
            hours: totalHoursSingle,
          },
    };
  }, [filteredWork, showRange]);

  const getPhaseTableData = (): TableRow[] => {
    return phases.map((phase) => {
      const { fee, gpm, hours, rawFee, rawHours } = calculatePhaseFinancials(
        phase,
        currencySymbol,
        showRange
      );

      // Calculate percentages
      const totalHoursNum = rangeUtils.getValue(totals.hours);
      const totalFeeNum = rangeUtils.getValue(totals.fee);

      let hourPercentage = "-";
      let feePercentage = "-";

      if (showRange) {
        const rawHoursVal = rangeUtils.getValue(rawHours);
        const rawFeeVal = rangeUtils.getValue(rawFee);
        hourPercentage =
          totalHoursNum > 0 ? `${((rawHoursVal / totalHoursNum) * 100).toFixed(1)}%` : "-";
        feePercentage = totalFeeNum > 0 ? `${((rawFeeVal / totalFeeNum) * 100).toFixed(1)}%` : "-";
      } else {
        const safeRawHours = typeof rawHours === "number" ? rawHours : 0;
        const safeRawFee = typeof rawFee === "number" ? rawFee : 0;
        hourPercentage =
          totalHoursNum > 0 ? `${((safeRawHours / totalHoursNum) * 100).toFixed(1)}%` : "-";
        feePercentage = totalFeeNum > 0 ? `${((safeRawFee / totalFeeNum) * 100).toFixed(1)}%` : "-";
      }

      const phaseRow: TableRow = {
        key: `phase-${phase.id}`,
        title: phase.title,
        startDate: phase.startDate ? new Date(phase.startDate).toLocaleDateString() : "-",
        endDate: phase.deadlineDate ? new Date(phase.deadlineDate).toLocaleDateString() : "-",
        rawStartDate: phase.startDate ? new Date(phase.startDate) : null,
        rawEndDate: phase.deadlineDate ? new Date(phase.deadlineDate) : null,
        fee,
        gpm,
        hours,
        hourPercentage,
        feePercentage,
        phaseId: phase.id,
      };
      return phaseRow;
    });
  };

  // Helper to calculate grouped work data for both practice area and fee earner level tables
  const calculateGroupTableData = (
    groupMap: Map<string, WorkGroup>,
    keyPrefix: string
  ): TableRow[] => {
    return Array.from(groupMap.entries())
      .map(([groupName, group]) => {
        let fee, gpm, hours, hourPercentage, feePercentage;

        if (showRange) {
          const totalFeeLower = group.work.reduce((sum, w) => sum + (w.price?.lower || 0), 0);
          const totalFeeUpper = group.work.reduce((sum, w) => sum + (w.price?.upper || 0), 0);
          const totalCostLower = group.work.reduce((sum, w) => sum + (w.cost?.lower || 0), 0);
          const totalCostUpper = group.work.reduce((sum, w) => sum + (w.cost?.upper || 0), 0);
          const totalHoursLower =
            group.work.reduce(
              (sum, w) =>
                sum + (w.timeLower != null ? w.timeLower : w.timeSingle != null ? w.timeSingle : 0),
              0
            ) / 60;
          const totalHoursUpper =
            group.work.reduce(
              (sum, w) =>
                sum + (w.timeUpper != null ? w.timeUpper : w.timeSingle != null ? w.timeSingle : 0),
              0
            ) / 60;

          fee = rangeUtils.formatRange(
            totalFeeLower,
            totalFeeUpper,
            (v) => `${currencySymbol}${v.toLocaleString()}`
          );

          const gpmLower = rangeUtils.calculateGpm(totalFeeLower, totalCostLower);
          const gpmUpper = rangeUtils.calculateGpm(totalFeeUpper, totalCostUpper);

          gpm = rangeUtils.formatRange(gpmLower, gpmUpper, (v) => `${Math.round(v)}%`);
          hours = rangeUtils.formatRange(totalHoursLower, totalHoursUpper, (v) => v.toFixed(1));

          const totalHoursNum = rangeUtils.getValue(totals.hours);
          const totalFeeNum = rangeUtils.getValue(totals.fee);

          hourPercentage =
            totalHoursNum > 0 ? `${((totalHoursLower / totalHoursNum) * 100).toFixed(1)}%` : "-";
          feePercentage =
            totalFeeNum > 0 ? `${((totalFeeLower / totalFeeNum) * 100).toFixed(1)}%` : "-";
        } else {
          fee = `${currencySymbol}${group.totalFee.toLocaleString()}`;
          gpm =
            group.totalFee > 0
              ? `${(((group.totalFee - group.totalCost) / group.totalFee) * 100).toFixed(1)}%`
              : "0%";
          hours = group.totalHours.toFixed(1);

          const totalHoursNum = rangeUtils.getValue(totals.hours);
          const totalFeeNum = rangeUtils.getValue(totals.fee);

          hourPercentage =
            totalHoursNum > 0 ? `${((group.totalHours / totalHoursNum) * 100).toFixed(1)}%` : "-";
          feePercentage =
            totalFeeNum > 0 ? `${((group.totalFee / totalFeeNum) * 100).toFixed(1)}%` : "-";
        }

        // Get unique fee earners for this group
        const feeEarners = new Set(group.work.map((w) => w.feeEarner?.name).filter(Boolean));

        return {
          key: `${keyPrefix}-${groupName}`,
          title: groupName,
          startDate: "-",
          endDate: "-",
          rawStartDate: null,
          rawEndDate: null,
          fee,
          gpm,
          hours,
          hourPercentage,
          feePercentage,
          level: `${feeEarners.size} fee earner${feeEarners.size === 1 ? "" : "s"}`,
        };
      })
      .sort((a, b) => b.title.localeCompare(a.title));
  };

  const getPracticeAreaTableData = (): TableRow[] => {
    return calculateGroupTableData(practiceAreaGroups, "practice-area");
  };

  const getCohortTableData = (): TableRow[] => {
    return calculateGroupTableData(feeEarnerLevelGroups, "fee-earner-level");
  };

  const getFeeEarnerTableData = (): TableRow[] => {
    return Array.from(feeEarnerGroups.entries())
      .map(([feeEarnerName, feeEarnerTotals]) => {
        const calculatedValues = calculateGroupFinancials(
          feeEarnerTotals,
          totals,
          currencySymbol,
          showRange
        );

        // Find fee earner level for this fee earner from the work data
        const feeEarnerLevel =
          filteredWork.find((work) => work.feeEarner?.name === feeEarnerName)?.feeEarnerLevel
            ?.description || "Unassigned";

        return {
          key: `fee-earner-${feeEarnerName}`,
          title: feeEarnerName,
          startDate: "-",
          endDate: "-",
          rawStartDate: null,
          rawEndDate: null,
          hours: calculatedValues.hours,
          hourPercentage: calculatedValues.hourPercentage,
          fee: calculatedValues.fee,
          feePercentage: calculatedValues.feePercentage,
          gpm: calculatedValues.gpm,
          level: feeEarnerLevel,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  };

  const titleColumnTitle = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span>Title</span>
    </div>
  );

  const columns = [
    {
      title: titleColumnTitle,
      dataIndex: "title",
      key: "title",
      render: (text: string, record: TableRow) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title:
        selectedTab === "by-phase"
          ? "Date Range"
          : selectedTab === "by-fee-earner"
          ? FEL_LABEL
          : "Fee Earners",
      key: "dateRange",
      width: "280px",
      render: (_: unknown, record: TableRow) => {
        if (selectedTab === "by-phase") {
          return (
            <RangePicker
              value={[
                record.rawStartDate ? dayjs(record.rawStartDate) : null,
                record.rawEndDate ? dayjs(record.rawEndDate) : null,
              ]}
              onChange={(dates) => {
                onUpdatePhase({
                  id: record.phaseId!,
                  startDate: dates?.[0]?.toDate() || null,
                  deadlineDate: dates?.[1]?.toDate() || null,
                });
              }}
              format={(value) => value?.toDate().toLocaleDateString() || ""}
              allowEmpty={[true, true]}
              style={{ width: 280 }}
            />
          );
        }
        return record.level || "-";
      },
    },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      align: "right" as const,
    },
    {
      title: "Hours %",
      dataIndex: "hourPercentage",
      key: "hourPercentage",
      align: "right" as const,
    },
    {
      title: "Fee",
      dataIndex: "fee",
      key: "fee",
      align: "right" as const,
    },
    {
      title: "Fee %",
      dataIndex: "feePercentage",
      key: "feePercentage",
      align: "right" as const,
    },
    {
      title: "GPM",
      dataIndex: "gpm",
      key: "gpm",
      align: "right" as const,
    },
  ];

  const renderSummaryRow = () => {
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={2}>
            <strong>Total</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">
            <strong>{rangeUtils.collapseRangeObject(totals.hours, (v) => v.toFixed(1))}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right">
            <strong>100%</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            <strong>
              {rangeUtils.collapseRangeObject(
                totals.fee,
                (v) => `${currencySymbol}${v.toLocaleString()}`
              )}
            </strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="right">
            <strong>100%</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} align="right">
            <strong>
              {showRange
                ? rangeUtils.formatGpmRange(
                    typeof totals.fee === "object" && totals.fee !== null ? totals.fee : {},
                    typeof totals.cost === "object" && totals.cost !== null ? totals.cost : {}
                  )
                : rangeUtils.getValue(totals.fee) > 0
                ? `${(
                    ((rangeUtils.getValue(totals.fee) - rangeUtils.getValue(totals.cost)) /
                      rangeUtils.getValue(totals.fee)) *
                    100
                  ).toFixed(1)}%`
                : "-"}
            </strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <Tabs defaultActiveKey="by-phase" onChange={setSelectedTab}>
      <TabPane tab="By Phase" key="by-phase">
        <Table
          columns={columns}
          dataSource={getPhaseTableData()}
          pagination={false}
          summary={renderSummaryRow}
          scroll={{ y: "calc(65vh - 120px)" }}
        />
      </TabPane>
      <TabPane tab={`By ${PA_LABEL}`} key="by-practice-area">
        <Table
          columns={columns}
          dataSource={getPracticeAreaTableData()}
          pagination={false}
          summary={renderSummaryRow}
          scroll={{ y: "calc(65vh - 120px)" }}
        />
      </TabPane>
      <TabPane tab={`By ${FE_LABEL}`} key="by-fee-earner">
        <Table
          columns={columns}
          dataSource={getFeeEarnerTableData()}
          pagination={false}
          summary={renderSummaryRow}
          scroll={{ y: "calc(65vh - 120px)" }}
        />
      </TabPane>
      <TabPane tab={`By ${FEL_LABEL}`} key="by-fee-earner-level">
        <Table
          columns={columns}
          dataSource={getCohortTableData()}
          pagination={false}
          summary={renderSummaryRow}
          scroll={{ y: "calc(65vh - 120px)" }}
        />
      </TabPane>
    </Tabs>
  );
};
